"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/navigation";
// ─────────────────────────────────────────────────────────────────────────────
// HEADLINE ANIMATION CONFIG
// TO SWAP STYLE — change this one constant:
//   "typewriter"     — full phrase types out, erases, repeats
//   "word-slide"     — prefix stays, keyword slides up to swap
//   "fade-emphasise" — prefix dims, keyword pulses, fades out
//   "scramble-fade"  — keyword scrambles in, prefix dims + glow, fades out
// ─────────────────────────────────────────────────────────────────────────────
type HeadlineStyle = "typewriter" | "word-slide" | "fade-emphasise" | "scramble-fade";
const HEADLINE_STYLE: HeadlineStyle = "fade-emphasise";

const KEYWORDS = [
  "those left behind",
  "dreamers",
  "home schoolers",
  "overachievers",
  "those who learn differently",
  "your path",
] as const;

// Personalised subline for each keyword — fades in after keyword resolves
const SUBLINES: Record<string, string> = {
  "dreamers":                    "Turn curiosity into real progress.",
  "those left behind":           "Close the gap before it gets bigger.",
  "home schoolers":              "A curriculum that adapts as they grow.",
  "overachievers":               "No ceiling. No waiting. Just what's next.",
  "those who learn differently": "Find the approach that actually clicks.",
  "your path":                   "Patharo finds it — then builds it with them.",
};

const SCRAMBLE_CONFIG = {
  framesPerChar:    4,
  leadInFrames:     6,
  tickMs:           110,
  emphasisScale:    1.06,
  emphasisGlow:     "0 0 28px rgba(96,165,250,0.65)",
  emphasisHoldMs:   700,
  fadeOutMs:        500,
  prefixDimOpacity: 0.1,
};
const TYPEWRITER_CONFIG = {
  prefixSpeedMs: 36, kwSpeedMs: 52, eraseKwMs: 28, erasePrefixMs: 20, holdMs: 2400,
};
const WORD_SLIDE_CONFIG = {
  prefixSpeedMs: 36, kwSpeedMs: 52, slideMs: 460, holdMs: 2800,
};
const FADE_EMPHASISE_CONFIG = {
  kwSpeedMs: 52, holdMs: 2000, emphasisScale: 1.07,
  emphasisHoldMs: 1400, prefixDimOpacity: 0.1, fadeOutMs: 380,
};

// ─────────────────────────────────────────────────────────────────────────────
// useHeadlineAnimation hook
// ─────────────────────────────────────────────────────────────────────────────
function useHeadlineAnimation(
  preRef:     React.RefObject<HTMLSpanElement>,
  kwRef:      React.RefObject<HTMLSpanElement>,
  dotRef:     React.RefObject<HTMLSpanElement>, // kept for API compat
  sublineRef: React.RefObject<HTMLParagraphElement>,
) {
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const kwIdx  = useRef(0);

  const tm = useCallback((fn: () => void, ms: number) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
    return id;
  }, []);

  const clr = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  const CHARS   = "abcdefghijklmnopqrstuvwxyz ";

  function showSubline(kw: string) {
    const el = sublineRef.current;
    if (!el) return;
    const line = SUBLINES[kw] ?? "";
    if (!line) return;
    el.textContent = line;
    el.style.opacity = "0";
    el.style.transform = "translateY(6px)";
    el.style.transition = "opacity 0.6s ease, transform 0.6s ease";
    timers.current.push(setTimeout(() => {
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    }, 80));
  }

  function hideSubline() {
    const el = sublineRef.current;
    if (!el) return;
    el.style.transition = "opacity 0.3s ease";
    el.style.opacity = "0";
  }
  const PREFIX  = "Know what to learn next, for ";
  const WRONG   = "Learn to know what next, for ";
  const PARTIAL = "Know what to learn";

  function renderPre(s: string) {
    const i = s.indexOf("next,");
    if (i < 0) return s;
    return s.slice(0, i) + `<em style="color:#60a5fa">${s.slice(i, i + 5)}</em>` + s.slice(i + 5);
  }

  function scrambleIn(el: HTMLSpanElement, word: string, onDone: () => void) {
    const { framesPerChar, leadInFrames, tickMs, emphasisScale, emphasisGlow, emphasisHoldMs } = SCRAMBLE_CONFIG;
    const tot = word.length * framesPerChar + leadInFrames + 8;
    let f = 0;
    function tick() {
      let s = "";
      for (let i = 0; i < word.length; i++) {
        s += f > i * framesPerChar + leadInFrames
          ? word[i]
          : CHARS[Math.floor(Math.random() * CHARS.length)];
      }
      el.textContent = s; f++;
      if (f < tot) { timers.current.push(setTimeout(tick, tickMs)); }
      else {
        el.textContent = word + ".";
        el.style.transition = `transform 0.4s cubic-bezier(0.16,1,0.3,1), text-shadow 0.4s`;
        el.style.transform   = `scale(${emphasisScale})`;
        el.style.textShadow  = emphasisGlow;
        timers.current.push(setTimeout(() => {
          el.style.transform  = "scale(1)";
          el.style.textShadow = "none";
          timers.current.push(setTimeout(onDone, 200));
        }, emphasisHoldMs));
      }
    }
    tick();
  }

  function doWrongOrder(onDone: () => void) {
    const p = preRef.current!; const k = kwRef.current!; const d = dotRef.current!;
    p.innerHTML = ""; k.textContent = ""; // dot removed
    k.style.cssText = ""; p.style.cssText = "";
    const wrongPart = "Learn to know what";
    let i = 0;
    function typeW() {
      const typed = WRONG.slice(0, i);
      p.innerHTML = typed.length <= wrongPart.length
        ? `<span style="color:#f87171">${typed}</span>`
        : `<span style="color:#f87171">${wrongPart}</span>${WRONG.slice(wrongPart.length, i)}`;
      i++;
      if (i <= WRONG.length) tm(typeW, 42); else typeFirstKw();
    }
    function typeFirstKw() {
      const kw = KEYWORDS[0]; let j = 0;
      k.style.cssText = "";
      function tick() {
        k.textContent = kw.slice(0, j) + "."; j++;
        if (j <= kw.length) tm(tick, 52);
        else tm(() => {
          const hl = p.closest(".hl") as HTMLElement;
          if (hl) { hl.style.animation = "shake 0.5s ease-in-out"; tm(() => { hl.style.animation = ""; }, 500); }
          // Strikethrough sweeps across the wrong part
          p.innerHTML = `<span style="position:relative;color:#f87171">${wrongPart}<span id="hc-strike" style="position:absolute;left:0;right:0;top:52%;height:2.5px;background:#f87171;transform:scaleX(0);transform-origin:left;transition:transform 0.4s ease"></span></span>${WRONG.slice(wrongPart.length)}`;
          tm(() => {
            const s = document.getElementById("hc-strike");
            if (s) s.style.transform = "scaleX(1)";
          }, 100);
          tm(() => {
            let ej = kw.length + 1;
            function eKw() {
              k.textContent = (kw + ".").slice(0, ej); ej--;
              if (ej >= 0) tm(eKw, 28);
              else { let ep = WRONG.length;
                function ePre() {
                  const t = WRONG.slice(0, ep);
                  p.innerHTML = t.length <= wrongPart.length
                    ? `<span style="color:#f87171">${t}</span>`
                    : `<span style="color:#f87171">${wrongPart}</span>${WRONG.slice(wrongPart.length, ep)}`;
                  ep--;
                  if (ep >= 0) tm(ePre, 20); else { p.innerHTML = ""; tm(onDone, 300); }
                }
                ePre();
              }
            }
            eKw();
          }, 1200);
        }, 600);
      }
      tick();
    }
    typeW();
  }

  function doMissingNext(onDone: () => void) {
    const p = preRef.current!; const k = kwRef.current!; const d = dotRef.current!;
    p.innerHTML = ""; k.textContent = ""; // dot removed
    let i = 0;
    function typePart() {
      p.innerHTML = PARTIAL.slice(0, i); i++;
      if (i <= PARTIAL.length) tm(typePart, 40);
      else tm(() => {
        p.innerHTML = `${PARTIAL}<span style="display:inline-block;overflow:hidden;vertical-align:bottom"><em style="display:block;transform:translateY(110%);transition:transform 0.5s cubic-bezier(0.16,1,0.3,1);color:#60a5fa" id="hc-next"> next,</em></span><span id="hc-for" style="opacity:0;transition:opacity 0.4s"> for </span>`;
        tm(() => {
          const ni = document.getElementById("hc-next");
          const fi = document.getElementById("hc-for");
          if (ni) ni.style.transform = "translateY(0)";
          tm(() => { if (fi) fi.style.opacity = "1"; }, 200);
          tm(onDone, 600);
        }, 80);
      }, 900);
    }
    typePart();
  }

  function doTypewriterCycle() {
    const p = preRef.current!; const k = kwRef.current!; const d = dotRef.current!;
    const { prefixSpeedMs, kwSpeedMs, eraseKwMs, erasePrefixMs, holdMs } = TYPEWRITER_CONFIG;
    const isLast = kwIdx.current === KEYWORDS.length - 1;
    const kw = KEYWORDS[kwIdx.current];
    p.innerHTML = ""; k.textContent = ""; // dot removed k.style.cssText = "";
    let i = 0;
    function typePre() { p.innerHTML = renderPre(PREFIX.slice(0, i)); i++; if (i <= PREFIX.length) tm(typePre, prefixSpeedMs); else typeKw(); }
    function typeKw() {
      let j = 0;
      function tick() { k.textContent = kw.slice(0, j); j++; if (j <= kw.length) tm(tick, kwSpeedMs); else done(); }
      tick();
    }
    function done() {
      k.textContent = kw + ".";
      showSubline(kw);
      if (isLast) return;
      tm(() => {
        hideSubline();
        let ej = kw.length + 1;
        function eKw() { k.textContent = (kw + ".").slice(0, ej); ej--; if (ej >= 0) tm(eKw, eraseKwMs); else ePre(); }
        eKw();
      }, holdMs);
    }
    function ePre() {
      let ep = PREFIX.length;
      function tick() { p.innerHTML = renderPre(PREFIX.slice(0, ep)); ep--; if (ep >= 0) tm(tick, erasePrefixMs); else { kwIdx.current = (kwIdx.current + 1) % KEYWORDS.length; tm(doTypewriterCycle, 250); } }
      tick();
    }
    typePre();
  }

  function doWordSlideInit() {
    const p = preRef.current!; const k = kwRef.current!; const d = dotRef.current!;
    const { prefixSpeedMs, kwSpeedMs, holdMs } = WORD_SLIDE_CONFIG;
    const kw = KEYWORDS[kwIdx.current];
    p.innerHTML = ""; k.textContent = ""; // dot removed
    k.style.cssText = "display:inline-block;transform:translateY(0);opacity:1";
    let i = 0;
    function typePre() { p.innerHTML = renderPre(PREFIX.slice(0, i)); i++; if (i <= PREFIX.length) tm(typePre, prefixSpeedMs); else typeKw(); }
    function typeKw() {
      let j = 0;
      function tick() { k.textContent = kw.slice(0, j); j++; if (j <= kw.length) tm(tick, kwSpeedMs); else { k.textContent = kw + "."; showSubline(kw); tm(doWordSlideSwap, holdMs); } }
      tick();
    }
    typePre();
  }
  function doWordSlideSwap() {
    const k = kwRef.current!;
    if (kwIdx.current === KEYWORDS.length - 1) return;
    hideSubline();
    kwIdx.current = (kwIdx.current + 1) % KEYWORDS.length;
    const next = KEYWORDS[kwIdx.current];
    const { slideMs, holdMs } = WORD_SLIDE_CONFIG;
    k.style.cssText = `display:inline-block;transform:translateY(-110%);opacity:0;transition:transform ${slideMs}ms cubic-bezier(0.16,1,0.3,1),opacity ${slideMs}ms`;
    tm(() => {
      k.style.cssText = `display:inline-block;transform:translateY(110%);opacity:0;transition:none`;
      k.textContent = next;
      tm(() => {
        k.style.cssText = `display:inline-block;transform:translateY(0);opacity:1;transition:transform ${slideMs}ms cubic-bezier(0.16,1,0.3,1),opacity ${slideMs}ms`;
        showSubline(next); // KEYWORDS entries have no period
        tm(doWordSlideSwap, holdMs);
      }, 40);
    }, slideMs);
  }

  function doFadeCycle() {
    const p = preRef.current!; const k = kwRef.current!; const d = dotRef.current!;
    const { kwSpeedMs, holdMs, emphasisScale, emphasisHoldMs, prefixDimOpacity, fadeOutMs } = FADE_EMPHASISE_CONFIG;
    const isLast = kwIdx.current === KEYWORDS.length - 1;
    const kw = KEYWORDS[kwIdx.current];
    k.textContent = ""; k.style.cssText = ""; // dot removed
    let j = 0;
    function typeKw() { k.textContent = kw.slice(0, j); j++; if (j <= kw.length) tm(typeKw, kwSpeedMs); else { k.textContent = kw + "."; done(); } }
    function done() {
      showSubline(kw);
      if (isLast) { p.style.cssText = "transition:opacity 0.8s;opacity:1;transform:scale(1)"; k.style.cssText = `transition:transform 0.45s cubic-bezier(0.16,1,0.3,1);transform:scale(${emphasisScale})`; return; }
      tm(() => {
        p.style.cssText = `transition:opacity 0.55s,transform 0.55s;opacity:${prefixDimOpacity};transform:scale(0.97)`;
        k.style.cssText = `transition:transform 0.45s cubic-bezier(0.16,1,0.3,1);transform:scale(${emphasisScale})`;
        tm(() => {
          hideSubline();
          k.style.cssText = `transition:opacity ${fadeOutMs}ms;opacity:0`;
          tm(() => { p.style.cssText = ""; k.style.cssText = ""; k.textContent = ""; kwIdx.current = (kwIdx.current + 1) % KEYWORDS.length; tm(doFadeCycle, 80); }, fadeOutMs);
        }, emphasisHoldMs);
      }, holdMs);
    }
    typeKw();
  }

  function doScrambleFadeCycle() {
    const p = preRef.current!; const k = kwRef.current!; const d = dotRef.current!;
    const { prefixDimOpacity, fadeOutMs } = SCRAMBLE_CONFIG;
    const isLast = kwIdx.current === KEYWORDS.length - 1;
    const kw = KEYWORDS[kwIdx.current];
    k.textContent = ""; k.style.cssText = ""; // dot removed
    scrambleIn(k, kw, () => {
      showSubline(kw);
      if (isLast) {
        p.style.cssText = "transition:opacity 0.8s,transform 0.8s;opacity:1;transform:scale(1)";
        k.style.cssText = `transform:scale(${SCRAMBLE_CONFIG.emphasisScale});text-shadow:${SCRAMBLE_CONFIG.emphasisGlow}`;
        return;
      }
      p.style.cssText = `transition:opacity 0.6s,transform 0.6s;opacity:${prefixDimOpacity};transform:scale(0.97)`;
      k.style.cssText = `transform:scale(${SCRAMBLE_CONFIG.emphasisScale});text-shadow:${SCRAMBLE_CONFIG.emphasisGlow}`;
      tm(() => {
        hideSubline();
        p.style.cssText = "transition:opacity 0.5s,transform 0.5s;opacity:1;transform:scale(1)";
        k.style.cssText = `transition:opacity ${fadeOutMs}ms;opacity:0;transform:scale(1);text-shadow:none`;
        tm(() => { k.style.cssText = ""; k.textContent = ""; kwIdx.current = (kwIdx.current + 1) % KEYWORDS.length; tm(doScrambleFadeCycle, 150); }, fadeOutMs + 50);
      }, 2200);
    });
  }

  function start() {
    clr(); kwIdx.current = 0;
    doWrongOrder(() => {
      doMissingNext(() => {
        kwIdx.current = 1; // start at 1 — dreamers already shown in wrong-order intro
        const runners: Record<HeadlineStyle, () => void> = {
          "typewriter":     doTypewriterCycle,
          "word-slide":     doWordSlideInit,
          "fade-emphasise": doFadeCycle,
          "scramble-fade":  doScrambleFadeCycle,
        };
        tm(runners[HEADLINE_STYLE], 400);
      });
    });
  }

  return { start, stop: clr };
}

// ─────────────────────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────────────────────

const HERO_SLIDES = [
  {
    tag: "For parents", tagColor: "#1549A3",
    statement: "Close learning gaps before they grow",
    description: "Spot weak areas early, give your child focused support, and keep progress moving in the right direction.",
    video: "/animations/sample_progress.png", panZoom: true,
    footerTags: ["Progress visible", "Gaps identified"],
    footerColors: ["#E0EAFA|#1549A3", "#D6F0E8|#0E6E54"],
  },
  {
    tag: "For older students", tagColor: "#6d28d9",
    statement: "See exactly where your child struggles",
    description: "Turn uncertainty into clear next steps with simple insight parents can actually use.",
    video: "/animations/patharo-demo.mp4", panZoom: false,
    footerTags: ["Any device", "Self-paced"],
    footerColors: ["#ede9fe|#6d28d9", "#f3e8ff|#7c3aed"],
  },
  {
    tag: "For young learners", tagColor: "#b45309",
    statement: "Build confidence, one concept at a time",
    description: "Help your child move from frustration to steady wins with learning that adapts as they improve.",
    video: "/animations/polo_and_kid.mp4", panZoom: false,
    footerTags: ["Ages 6–11", "Polo guided"],
    footerColors: ["#fef9ec|#92400e", "#D6F0E8|#0E6E54"],
  },
];

const DEMO_PROFILES = [
  { id: "math-g6", label: "Grade 6 · Math", student: "Ava", grade: "Grade 6", subject: "Math", strengths: ["Addition", "Basic fractions", "Number sense"], needs: ["Word problems", "Multi-step equations", "Decimal comparison"], plan: ["Practice two-step word problems for 10 minutes a day", "Review equation setup before solving", "Use visual fraction and decimal models 3× this week"], progress: 65, improvement: "+15% this week", nextFocus: "Multi-step problem solving" },
  { id: "reading-g4", label: "Grade 4 · Reading", student: "Noah", grade: "Grade 4", subject: "Reading", strengths: ["Phonics", "Vocabulary", "Reading stamina"], needs: ["Inference", "Main idea", "Written response"], plan: ["Read one short passage and identify the main idea", "Answer one inference question using text evidence", "Write a 3-sentence response after each reading session"], progress: 58, improvement: "+11% this week", nextFocus: "Finding evidence in the text" },
  { id: "science-g8", label: "Grade 8 · Science", student: "Maya", grade: "Grade 8", subject: "Science", strengths: ["Vocabulary", "Diagrams", "Recall"], needs: ["Cause and effect", "Lab conclusions", "Applying concepts"], plan: ["Review one concept map before each lesson", "Explain one science idea in plain language daily", "Practice short written conclusions using experiment results"], progress: 72, improvement: "+18% this week", nextFocus: "Applying concepts to new questions" },
];

const PAIN_POINTS = [
  { q: "His teacher says he's doing fine.", sub: "But you have no idea what that actually means for the skills he needs next." },
  { q: "She studied for an hour and still failed the test.", sub: "Something isn't connecting — but what, exactly?" },
  { q: "Every app gives them the same lessons as everyone else.", sub: "Your child doesn't learn like every other kid." },
  { q: "French Immersion is great — but I can't help at home.", sub: "Patharo guides them in both languages, independently." },
];

const LEARNING_STYLES = [
  { emoji: "👁️", label: "Visual learner", desc: "Diagrams and visual breakdowns that make abstract concepts click." },
  { emoji: "✍️", label: "Step-by-step", desc: "Methodical practice that builds confidence one piece at a time." },
  { emoji: "🔁", label: "Learns by doing", desc: "Hands-on repetition with instant feedback until it sticks." },
  { emoji: "💬", label: "Needs to know why", desc: "Deeper explanations — not just the answer, but the reasoning." },
];

const GRADES = ["Grade 1","Grade 2","Grade 3","Grade 4","Grade 5","Grade 6","Grade 7","Grade 8","Grade 9","Grade 10","Grade 11","Grade 12"];
const ROTATE_MS = 8000;

const FAQ_ITEMS = [
  { q: "Is this replacing my child's teacher?", a: "Not at all. Patharo is designed to support what's happening in the classroom — not replace it. Think of it as the extra 10 minutes of focused practice that makes the teacher's lessons actually stick. Polo follows the Ontario curriculum so everything your child does is directly relevant to what they're learning at school." },
  { q: "What if my child is in French Immersion?", a: "Patharo is built with French Immersion families in mind. We support both English and French content across subjects, so your child can practise in the language they're being taught — without you needing to be fluent yourself." },
  { q: "How is this different from other apps?", a: "Most platforms give every child the same content. Patharo finds the specific gap your child has right now and builds a path from there. It also gives parents a clear view of progress by subject and skill — not just a score or a badge." },
  { q: "What grades does Patharo cover?", a: "Patharo covers Grades 1 through 12, aligned to the Ontario curriculum. We're building content grade by grade and prioritising based on what families on the waitlist tell us they need most." },
  { q: "How much does it cost?", a: "Patharo is free to start. We're still in early access and working with families to shape the product before we introduce any paid plans. Everyone on the waitlist gets free access during this period." },
  { q: "My child is frustrated with learning. Will this make it worse?", a: "We hear this a lot — and it's exactly why Polo exists. Polo is patient, warm, and never makes a child feel bad for not knowing something. Progress is celebrated at every step, and sessions are short by design. Most kids respond well because it doesn't feel like homework." },
];

// ─────────────────────────────────────────────────────────────────────────────
// CHART COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function GapsChart() {
  const [step, setStep] = useState(0);
  useEffect(() => { const t = setInterval(() => setStep((s) => (s + 1) % 5), 900); return () => clearInterval(t); }, []);
  const answers = [
    { y: 14, fill: "#D6F0E8", stroke: "#1A9E78", textFill: "#0E6E54", label: "1¼  ✓" },
    { y: 48, fill: "#FFE8E8", stroke: "#E05555", textFill: "#A02020", label: "1  ✗" },
    { y: 82, fill: "#FFE8E8", stroke: "#E05555", textFill: "#A02020", label: "2  ✗" },
  ];
  return (
    <svg viewBox="0 0 280 140" width="100%" className="block">
      <rect x="10" y="46" width="100" height="34" rx="8" fill="#E0EAFA" stroke="#2264D1" strokeWidth="1.5"/>
      <text x="60" y="67" textAnchor="middle" fontSize="11" fontFamily="system-ui,sans-serif" fill="#1549A3">What is ¾ + ½?</text>
      {answers.map((a, i) => (<line key={i} x1="110" y1="63" x2="132" y2={a.y + 14} stroke="#A8BCCE" strokeWidth="1" strokeDasharray="4,3" opacity={step > i ? 1 : 0} style={{ transition: "opacity 0.3s" }}/>))}
      {answers.map((a, i) => (<g key={i} style={{ opacity: step > i ? 1 : 0, transition: "opacity 0.4s" }}><rect x="132" y={a.y} width="72" height="28" rx="7" fill={a.fill} stroke={a.stroke} strokeWidth="1.5"/><text x="168" y={a.y + 18} textAnchor="middle" fontSize="11" fontFamily="system-ui,sans-serif" fill={a.textFill}>{a.label}</text></g>))}
      <circle cx="230" cy="63" r={step >= 4 ? 28 : 0} fill="none" stroke="#2264D1" strokeWidth="1.5" strokeDasharray="6,3" opacity="0.5" style={{ transition: "r 0.5s ease" }}/>
      {step >= 4 && (<><text x="230" y="58" textAnchor="middle" fontSize="10" fontFamily="system-ui,sans-serif" fill="#2264D1" fontWeight="600">gap</text><text x="230" y="72" textAnchor="middle" fontSize="10" fontFamily="system-ui,sans-serif" fill="#2264D1" fontWeight="600">found</text></>)}
    </svg>
  );
}

function PathChart() {
  const [step, setStep] = useState(0);
  useEffect(() => { const t = setInterval(() => setStep((s) => (s >= 5 ? 0 : s + 1)), 700); return () => clearInterval(t); }, []);
  return (
    <svg viewBox="0 0 280 140" width="100%" className="block">
      <line x1="55" y1="62" x2="96" y2="44" stroke="#A8BCCE" strokeWidth="1.5" strokeDasharray="4,3"/>
      <line x1="55" y1="70" x2="96" y2="70" stroke="#A8BCCE" strokeWidth="1.5" strokeDasharray="4,3"/>
      <line x1="55" y1="78" x2="96" y2="96" stroke="#A8BCCE" strokeWidth="1.5" strokeDasharray="4,3"/>
      <line x1="55" y1="62" x2="96" y2="44" stroke="#1A9E78" strokeWidth="2" opacity={step >= 1 ? 1 : 0} style={{ transition: "opacity 0.4s" }}/>
      <line x1="124" y1="36" x2="167" y2="36" stroke="#1A9E78" strokeWidth="2" opacity={step >= 2 ? 1 : 0} style={{ transition: "opacity 0.4s" }}/>
      <circle cx="40" cy="70" r="16" fill="#1A9E78"/><text x="40" y="74" textAnchor="middle" fontSize="9" fontFamily="system-ui,sans-serif" fill="white">Start</text>
      {[{ cx: 110, cy: 36, label: "Visual", lit: step >= 1 }, { cx: 110, cy: 70, label: "Steps", lit: false }, { cx: 110, cy: 104, label: "Practice", lit: false }].map((n) => (<g key={n.label}><circle cx={n.cx} cy={n.cy} r="13" fill={n.lit ? "#D6F0E8" : "#EDF2F8"} stroke={n.lit ? "#1A9E78" : "#A8BCCE"} strokeWidth="1.5" style={{ transition: "fill 0.4s, stroke 0.4s" }}/><text x={n.cx} y={n.cy + 4} textAnchor="middle" fontSize="8" fontFamily="system-ui,sans-serif" fill={n.lit ? "#0E6E54" : "#5A708A"}>{n.label}</text></g>))}
      <circle cx="180" cy="36" r="13" fill={step >= 2 ? "#1A9E78" : "#EDF2F8"} stroke={step >= 2 ? "#1A9E78" : "#A8BCCE"} strokeWidth="1.5" style={{ transition: "fill 0.5s, stroke 0.5s" }}/>
      <text x="180" y="40" textAnchor="middle" fontSize="8" fontFamily="system-ui,sans-serif" fill={step >= 2 ? "white" : "#5A708A"}>Next</text>
      <rect x="200" y="22" width="68" height="24" rx="6" fill="#0E6E54" opacity={step >= 3 ? 1 : 0} style={{ transition: "opacity 0.4s" }}/>
      <text x="234" y="37" textAnchor="middle" fontSize="9" fontFamily="system-ui,sans-serif" fill="white" opacity={step >= 3 ? 1 : 0} style={{ transition: "opacity 0.4s" }}>your path ✓</text>
    </svg>
  );
}

function ProgressChart() {
  const [progress, setProgress] = useState(0);
  useEffect(() => { const t = setInterval(() => setProgress((p) => (p >= 100 ? 0 : p + 1)), 60); return () => clearInterval(t); }, []);
  const pts = [{ x: 30, y: 108 }, { x: 78, y: 92 }, { x: 118, y: 78 }, { x: 158, y: 58 }, { x: 198, y: 40 }, { x: 238, y: 20 }, { x: 258, y: 10 }];
  const total = pts.length - 1;
  const drawn = (progress / 100) * total;
  const fullPts = pts.slice(0, Math.floor(drawn) + 1);
  const frac = drawn - Math.floor(drawn);
  const last = fullPts[fullPts.length - 1];
  const next = pts[Math.min(Math.floor(drawn) + 1, pts.length - 1)];
  const tip = { x: last.x + (next.x - last.x) * frac, y: last.y + (next.y - last.y) * frac };
  const allPts = [...fullPts.slice(0, -1), tip];
  const polyStr = allPts.map((p) => `${p.x},${p.y}`).join(" ");
  const areaStr = polyStr + ` ${tip.x},118 30,118`;
  const labels = ["Wk 1", "Wk 2", "Wk 3", "Wk 4", "Wk 5", "Now"];
  return (
    <svg viewBox="0 0 290 130" width="100%" className="block">
      {[0, 1, 2, 3].map((i) => (<line key={i} x1="24" y1={14 + i * 32} x2="270" y2={14 + i * 32} stroke="#C8D8EE" strokeWidth="0.5"/>))}
      {["100%", "75%", "50%", "25%"].map((l, i) => (<text key={l} x="20" y={18 + i * 32} textAnchor="end" fontSize="8" fontFamily="system-ui,sans-serif" fill="#8AAAC8">{l}</text>))}
      {allPts.length > 1 && (<polygon points={areaStr} fill="#2264D1" opacity="0.07"/>)}
      {allPts.length > 1 && (<polyline points={polyStr} fill="none" stroke="#2264D1" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>)}
      {pts.slice(0, Math.floor(drawn) + 1).map((p, i) => (<circle key={i} cx={p.x} cy={p.y} r={i === Math.floor(drawn) && progress >= 99 ? 6 : 4} fill={progress >= 99 && i === pts.length - 1 ? "#2264D1" : "white"} stroke="#2264D1" strokeWidth="2"/>))}
      {progress >= 99 && (<><rect x="238" y="0" width="38" height="18" rx="4" fill="#2264D1"/><text x="257" y="13" textAnchor="middle" fontSize="9" fontFamily="system-ui,sans-serif" fill="white" fontWeight="600">91%</text></>)}
      {labels.map((l, i) => (<text key={l} x={30 + i * 46} y="126" textAnchor="middle" fontSize="8" fontFamily="system-ui,sans-serif" fill="#8AAAC8">{l}</text>))}
    </svg>
  );
}

function ProgressRing({ value }: { value: number }) {
  const r = 28; const circ = 2 * Math.PI * r;
  return (
    <div className="relative flex h-16 w-16 items-center justify-center">
      <svg className="h-16 w-16 -rotate-90" viewBox="0 0 64 64" fill="none">
        <circle cx="32" cy="32" r={r} stroke="#DDE6F4" strokeWidth="6" />
        <circle cx="32" cy="32" r={r} stroke="#2264D1" strokeWidth="6" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ - (value / 100) * circ}/>
      </svg>
      <div className="absolute text-xs font-semibold text-slate-800">{value}%</div>
    </div>
  );
}

function FadeIn({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s` }}>
      {children}
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-2xl border bg-white transition-shadow ${open ? "border-slate-200 shadow-sm" : "border-slate-100"}`}>
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-start justify-between gap-4 px-6 py-5 text-left">
        <span className="font-serif text-base font-medium text-slate-900">{q}</span>
        <span className={`mt-0.5 shrink-0 text-slate-400 transition-transform duration-200 ${open ? "rotate-45" : ""}`}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="8" y1="2" x2="8" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><line x1="2" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (<motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: "easeOut" }} className="overflow-hidden"><p className="px-6 pb-5 text-sm leading-7 text-slate-500">{a}</p></motion.div>)}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const [index, setIndex]           = useState(0);
  const [heroVisible, setHeroVisible] = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const [heroStep, setHeroStep]     = useState<1 | 2 | 3 | 4>(1);
  const [heroEmail, setHeroEmail]   = useState("");
  const [heroLoading, setHeroLoading] = useState(false);
  const [email2, setEmail2]         = useState("");
  const [grade2, setGrade2]         = useState("");
  const [sub2Done, setSub2Done]     = useState(false);
  const [sub2Loading, setSub2Loading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(DEMO_PROFILES[0]);
  
  const router = useRouter();

  useEffect(() => {
    fetch("/api/auth/me", { credentials: "include", cache: "no-store" })
      .then(r => { if (r.ok) router.replace("/welcome"); })
      .catch(() => {});
  }, []);
  
  // Headline animation refs
  const preRef    = useRef<HTMLSpanElement>(null);
  const kwRef = useRef<HTMLSpanElement | null>(null);
  const dotRef    = useRef<HTMLSpanElement>(null); // kept for API compat
  const sublineRef = useRef<HTMLParagraphElement>(null);
  const { start: startHeadline, stop: stopHeadline } = useHeadlineAnimation(preRef, kwRef, dotRef, sublineRef);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => { clearTimeout(t); window.removeEventListener("scroll", onScroll); };
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => setIndex((prev) => (prev + 1) % HERO_SLIDES.length), ROTATE_MS);
    return () => window.clearInterval(interval);
  }, []);

  // Start headline animation once hero is visible
  useEffect(() => {
    if (heroVisible) { startHeadline(); return stopHeadline; }
  }, [heroVisible]);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoCallbackRef = (el: HTMLVideoElement | null) => {
    videoRef.current = el;
    if (el) { el.load(); el.play().catch(() => {}); }
  };

  const current = useMemo(() => HERO_SLIDES[index], [index]);

  const tv = (d: number): React.CSSProperties => ({
    opacity: heroVisible ? 1 : 0,
    transform: heroVisible ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.8s ease ${d}s, transform 0.8s ease ${d}s`,
  });

  async function handleHeroEmail() {
    if (!heroEmail || !heroEmail.includes("@")) return;
    setHeroLoading(true);
    try { await fetch("/api/waitlist", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: heroEmail, grade: "", source: "hero" }) }); } catch {}
    setHeroLoading(false);
    setHeroStep(3);
  }

  async function handleHeroGrade(g: string) {
    fetch("/api/waitlist", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: heroEmail, grade: g, source: "hero" }) }).catch(() => {});
    setHeroStep(4);
  }

  async function handleWaitlist() {
    if (!email2 || !email2.includes("@")) return;
    setSub2Loading(true);
    try { await fetch("/api/waitlist", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: email2, grade: grade2, source: "waitlist" }) }); } catch {}
    setSub2Loading(false);
    setSub2Done(true);
  }

  return (
    <main className="min-h-screen overflow-x-hidden bg-[linear-gradient(135deg,_#f8fbff_0%,_#eef7ff_100%)] text-slate-900">

      {/* ── NAV ── */}
      <nav className={`sticky top-0 z-50 transition-shadow duration-300 ${scrolled ? "bg-white/90 shadow-[0_1px_0_#DDE6F4,0_4px_20px_rgba(14,22,38,0.07)] backdrop-blur-md" : "bg-transparent"}`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 font-bold text-white">P</div>
            <span className="text-lg font-semibold tracking-tight">Patharo</span>
          </div>
          <div className="flex items-center gap-4">
            <Link href="/about" className="hidden text-sm font-medium text-slate-500 hover:text-slate-800 sm:block">About</Link>
            <Link href="/blog" className="hidden text-sm font-medium text-slate-500 hover:text-slate-800 sm:block">Blog</Link>
            <Link href="/login" className="text-sm font-semibold text-blue-700 hover:text-blue-800">Log in</Link>
            <Link href="/onboarding" className="rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-blue-500/20 transition hover:shadow-lg hover:shadow-blue-500/30">Create your plan</Link>
          </div>
        </div>
      </nav>

      {/* ── HERO — OPTION C ── */}
      <section className="relative overflow-hidden min-h-screen lg:h-screen" style={{ minHeight: 680, maxHeight: "none" }}>

        {/* Background */}
        <div className="absolute inset-0">
          <AnimatePresence mode="wait">
            <motion.div key={current.video} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.9, ease: "easeInOut" }} className="absolute inset-0">
              {/\.(png|jpe?g|gif|webp|avif|svg)$/i.test(current.video) ? (
                <div className="absolute flex items-center justify-center" style={{ top: 0, left: 0, right: 0, bottom: "-60px", overflow: "hidden" }}>
                    <img
                      src={current.video}
                      alt=""
                      className={current.panZoom ? "hero-bg-pan" : ""}
                      style={{
                        width: "100%",
                        height: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        objectPosition: "70% center",
                        transformOrigin: "center",
                        display: "block",
                      }}
                    />
                  </div>
              ) : (
                <video ref={videoCallbackRef} key={current.video} src={current.video} autoPlay muted playsInline loop preload="metadata" style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: "-60px", width: "100%", height: "calc(100% + 60px)", objectFit: "contain", objectPosition: "50% center" }} />
              )}
            </motion.div>
          </AnimatePresence>
          <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, rgba(6,10,22,0.80) 0%, rgba(6,10,22,0.65) 35%, rgba(6,10,22,0.18) 68%, rgba(6,10,22,0.02) 100%)" }} />
          <div className="absolute bottom-0 left-0 right-0 h-28" style={{ background: "linear-gradient(to top, rgba(6,10,22,0.92) 0%, rgba(6,10,22,0.6) 40%, transparent 100%)" }} />
        </div>

        {/* Copy */}
        <div className="relative z-10 max-w-7xl px-6 pl-14 pt-16 pb-28 sm:pl-16 lg:pl-24 lg:pt-20 lg:pb-36">
          {/* Headline — in normal flow */}
          <div className="max-w-lg">
{/* Animated headline + subline */}
            <div style={{ ...tv(0.12), minHeight: "11rem" }} className="mt-4">
              <h1 className="hl font-semibold leading-tight tracking-[-0.04em] text-white"
                style={{ fontSize: "clamp(36px, 5vw, 58px)", fontFamily: "'Spectral', serif", fontWeight: 500 }}>
                <span ref={preRef} />
                <span ref={kwRef} className="text-blue-400" />
              </h1>
              <style>{`
                @keyframes shake { 0%,100%{transform:translateX(0)} 15%{transform:translateX(-6px)} 30%{transform:translateX(5px)} 45%{transform:translateX(-4px)} 60%{transform:translateX(3px)} 75%{transform:translateX(-2px)} }
                @keyframes heroBgPan { 0%,15%{transform:scale(1.0) translate(0%,0%)} 35%,55%{transform:scale(1.65) translate(-10%,5%)} 70%,85%{transform:scale(1.55) translate(8%,-6%)} 100%{transform:scale(1.0) translate(0%,0%)} }
                .hero-bg-pan { animation: heroBgPan 20s ease-in-out infinite; will-change: transform; transform-origin: center; }
                .hl em { font-style: italic; color: inherit; }
                
              `}</style>
            </div>
          </div>

          {/* Rotating subline — personalised per keyword */}
          <div style={{ position: "absolute", top: "calc(4rem + 1rem + 11rem + 6rem)", left: 0, right: 0, zIndex: 10 }}
            className="max-w-lg px-6 pl-16 sm:pl-18 lg:pl-28">
            <p ref={sublineRef}
              className="text-sm font-medium text-blue-300/80"
              style={{ opacity: 0, minHeight: "1.4rem", transition: "opacity 0.6s ease, transform 0.6s ease" }}
            />
          </div>

          {/* CTA + subtext + pills — absolutely positioned so headline height never affects them */}
          <div style={{ position: "absolute", top: "calc(4rem + 1rem + 11rem + 12rem)", left: 0, right: 0 }}
            className="max-w-lg px-6 pl-16 sm:pl-18 lg:pl-28">

            {/* CTA steps */}
            <div style={tv(0.28)}>
              {heroStep === 1 && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                  <button onClick={() => setHeroStep(2)} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_8px_28px_rgba(37,99,235,0.4)] transition hover:shadow-[0_10px_36px_rgba(37,99,235,0.55)] hover:-translate-y-0.5">
                    Join waitlist
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
                  </button>
                  <p className="mt-3 text-xs text-white/35">Free · Takes 2 minutes · No commitment</p>
                </motion.div>
              )}
              {heroStep === 2 && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                  <p className="mb-3 text-sm font-medium text-white/80">What&apos;s your email address?</p>
                  <div className="flex gap-2 max-w-md">
                    <input type="email" placeholder="you@example.com" value={heroEmail} autoFocus onChange={(e) => setHeroEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleHeroEmail()} className="h-12 flex-1 rounded-xl border border-white/20 bg-white/10 px-4 text-sm text-white outline-none placeholder:text-white/35 focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30 backdrop-blur-sm"/>
                    <button onClick={handleHeroEmail} disabled={heroLoading || !heroEmail} className="h-12 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(37,99,235,0.35)] transition hover:opacity-90 disabled:opacity-50">{heroLoading ? "…" : "Next →"}</button>
                  </div>
                  <p className="mt-2 text-xs text-white/35">Free · No commitment · We&apos;ll reach out first</p>
                </motion.div>
              )}
              {heroStep === 3 && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                  <p className="mb-3 text-sm font-medium text-white/80">One quick thing — what grade is your child?</p>
                  <div className="flex flex-wrap gap-2 max-w-md">
                    {GRADES.map((g) => (<button key={g} onClick={() => handleHeroGrade(g)} className="rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/80 transition hover:border-blue-400 hover:bg-white/20 hover:text-white">{g}</button>))}
                    <button onClick={() => setHeroStep(4)} className="rounded-lg border border-white/10 bg-transparent px-3 py-1.5 text-xs text-white/30 transition hover:text-white/60">Skip</button>
                  </div>
                </motion.div>
              )}
              {(heroStep as number) >= 4 && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="rounded-xl bg-emerald-500/20 border border-emerald-400/30 px-5 py-3.5 text-sm font-medium text-emerald-300 max-w-md">
                  🎉 You&apos;re on the list — we&apos;ll be in touch first.
                </motion.div>
              )}
            </div>

            {/* Subtext */}
            <div style={tv(0.35)} className="mt-8">
              <p className="text-sm leading-relaxed text-white/80 max-w-sm">
                Is your child falling behind?{" "}
                <span className="text-white/60">Most parents only find out when it&apos;s already a bigger problem.</span>
              </p>
            </div>

            {/* Trust pills */}
            <div style={tv(0.38)} className="mt-6">
              <div className="flex flex-wrap gap-2">
                {["✓ Ontario curriculum aligned", "✓ French Immersion", "✓ Grades 1–12"].map((t) => (<span key={t} className="rounded-full border border-white/30 bg-white/10 px-3 py-1 text-xs font-semibold text-white/80 backdrop-blur-sm">{t}</span>))}
              </div>
            </div>
          </div>
        </div>

        {/* Slide dots */}
        <div className="absolute bottom-7 right-7 z-10 flex items-center gap-2">
          <span className="text-xs text-white/35 mr-1">Live preview</span>
          {HERO_SLIDES.map((_, i) => (<button key={i} type="button" onClick={() => setIndex(i)} className={`h-1.5 rounded-full transition-all ${i === index ? "w-6 bg-white" : "w-1.5 bg-white/30 hover:bg-white/60"}`} />))}
        </div>

        {/* Footer tags */}
        <div className="absolute bottom-7 left-8 z-10 lg:left-20">
          <AnimatePresence mode="wait">
            <motion.div key={index} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="flex gap-2">
              {current.footerTags.map((tag, i) => { const [bg, color] = current.footerColors[i].split("|"); return <span key={tag} className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide" style={{ background: bg, color }}>{tag}</span>; })}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── ORNAMENT ── */}
      <div className="bg-slate-900 py-10 text-center font-serif text-sm tracking-[0.3em] text-white/20">— ✦ —</div>

      {/* ── PAIN POINTS ── */}
      <section className="relative overflow-hidden bg-slate-900 px-6 py-20">
        <div className="pointer-events-none absolute right-10 top-0 select-none font-serif text-[200px] font-semibold leading-none text-white/[0.025]" aria-hidden>?</div>
        <div className="mx-auto max-w-5xl">
          <FadeIn><span className="mb-4 inline-block rounded-full bg-white/[0.08] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-200/80">Sound familiar?</span></FadeIn>
          <FadeIn delay={0.08}><h2 className="mb-10 max-w-lg font-serif text-3xl font-normal leading-snug text-white md:text-4xl">Things parents say — before they find Patharo.</h2></FadeIn>
          <div className="grid gap-3 sm:grid-cols-2">
            {PAIN_POINTS.map((p, i) => (<FadeIn key={i} delay={0.1 + i * 0.07}><div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition hover:bg-white/[0.07]"><p className="font-serif text-base italic leading-relaxed text-white">&ldquo;{p.q}&rdquo;</p><p className="mt-2.5 text-sm leading-relaxed text-white/40">{p.sub}</p></div></FadeIn>))}
          </div>
          <FadeIn delay={0.44}><div className="mt-10 border-t border-white/10 pt-8"><p className="font-serif text-lg font-light italic leading-relaxed text-white/90 md:text-xl">Patharo was built for exactly this — the gap between <span className="font-medium not-italic text-blue-300">&ldquo;doing fine&rdquo;</span> and actually knowing what to do next.</p></div></FadeIn>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <FadeIn><div className="mb-12 flex items-baseline gap-5"><h2 className="whitespace-nowrap font-serif text-3xl font-normal tracking-tight md:text-4xl">How it works</h2><div className="h-px flex-1 bg-slate-200" /></div></FadeIn>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { title: "Finds gaps", num: "01", bg: "from-blue-50 to-blue-100/60", chart: <GapsChart />, desc: "Short, targeted questions pinpoint exactly what needs work — not a generic \"needs improvement\". Patharo sees the pattern before it becomes a bigger problem." },
            { title: "Builds your path", num: "02", bg: "from-emerald-50 to-emerald-100/60", chart: <PathChart />, desc: "No two kids learn the same way. Patharo adapts the next step to how your child thinks and works best — then keeps adjusting as they grow." },
            { title: "Tracks progress", num: "03", bg: "from-blue-50 to-cyan-50/60", chart: <ProgressChart />, desc: "Watch the path unfold over time. Clear, honest progress for both students and parents — so you always know things are moving in the right direction." },
          ].map((card, i) => (
            <FadeIn key={card.title} delay={i * 0.1}>
              <div className="flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className={`flex h-64 items-center justify-center bg-gradient-to-br ${card.bg} p-8`}>{card.chart}</div>
                <div className="flex flex-1 flex-col p-7">
                  <div className="mb-3 flex items-center justify-between"><h3 className="font-serif text-xl font-medium">{card.title}</h3><span className="text-xs tracking-widest text-slate-300">{card.num}</span></div>
                  <p className="text-sm leading-7 text-slate-500">{card.desc}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── DEMO PROFILES ── */}
      <section className="mx-auto max-w-6xl px-6 pb-20">
        <FadeIn><div className="mb-3 text-center"><h2 className="font-serif text-3xl font-normal tracking-tight md:text-4xl">See your child&apos;s personalized plan</h2><p className="mt-3 text-sm text-slate-500">Click through sample profiles to see the kind of clarity parents get right away.</p></div></FadeIn>
        <FadeIn delay={0.1}><div className="mt-6 mb-5 flex flex-wrap justify-center gap-2">{DEMO_PROFILES.map((p) => (<button key={p.id} onClick={() => setSelectedProfile(p)} className={`rounded-full px-4 py-2 text-sm font-medium transition ${p.id === selectedProfile.id ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{p.label}</button>))}</div></FadeIn>
        <AnimatePresence mode="wait">
          <motion.div key={selectedProfile.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3, ease: "easeOut" }} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_16px_48px_rgba(15,23,42,0.08)] md:p-8">
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-5">
                <div className="flex items-start justify-between gap-4 rounded-2xl bg-slate-50 p-5">
                  <div><p className="text-xs text-slate-400">Student snapshot</p><p className="mt-0.5 text-lg font-semibold text-slate-900">{selectedProfile.student} · {selectedProfile.grade}</p><p className="text-sm text-slate-500">Subject: {selectedProfile.subject}</p><span className="mt-2 inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">Next: {selectedProfile.nextFocus}</span></div>
                  <ProgressRing value={selectedProfile.progress} />
                </div>
                <div><p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Strengths</p><div className="flex flex-wrap gap-2">{selectedProfile.strengths.map((s) => (<span key={s} className="rounded-full bg-emerald-50 px-3 py-1 text-xs text-emerald-700">{s}</span>))}</div></div>
                <div><p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Needs attention</p><div className="flex flex-wrap gap-2">{selectedProfile.needs.map((s) => (<span key={s} className="rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-700">{s}</span>))}</div></div>
              </div>
              <div className="space-y-4">
                <div className="rounded-2xl bg-slate-50 p-5">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Recommended plan for this week</p>
                  <div className="space-y-2">{selectedProfile.plan.map((step, idx) => (<div key={step} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3"><div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[11px] font-bold text-white">{idx + 1}</div><p className="pt-0.5 text-sm text-slate-700">{step}</p></div>))}</div>
                </div>
                <div className="rounded-2xl bg-slate-50 p-5">
                  <div className="mb-2 flex items-center justify-between"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Weekly progress</p><span className="text-xs font-semibold text-blue-600">{selectedProfile.improvement}</span></div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-200"><motion.div initial={{ width: 0 }} animate={{ width: `${selectedProfile.progress}%` }} transition={{ duration: 0.7, ease: "easeOut" }} className="h-2.5 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500"/></div>
                  <p className="mt-2.5 text-xs leading-relaxed text-slate-400">Your child&apos;s plan adapts as they improve, so next steps stay focused and realistic.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
        <FadeIn delay={0.1}><div className="mt-8 text-center"><Link href="/onboarding" className="inline-block rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-8 py-3.5 font-semibold text-white shadow-lg shadow-blue-500/20 transition hover:shadow-xl">Create your child&apos;s plan</Link></div></FadeIn>
      </section>

      {/* ── EVERY PATH IS DIFFERENT ── */}
      <section className="border-y border-slate-200 bg-blue-50/60 px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <FadeIn><div className="mb-8 flex items-baseline gap-5"><h2 className="whitespace-nowrap font-serif text-2xl font-normal tracking-tight md:text-3xl">Every child has their own path.</h2><div className="h-px flex-1 bg-blue-200/60" /></div></FadeIn>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {LEARNING_STYLES.map((s, i) => (<FadeIn key={s.label} delay={i * 0.07}><div className="h-full rounded-3xl border border-slate-200 bg-white p-7 transition hover:-translate-y-1 hover:shadow-md"><div className="mb-5 text-3xl">{s.emoji}</div><p className="mb-2 font-serif text-base font-medium text-slate-900">{s.label}</p><p className="text-sm leading-relaxed text-slate-500">{s.desc}</p></div></FadeIn>))}
          </div>
          <FadeIn delay={0.3}><p className="mt-5 text-xs italic text-slate-400">Polo figures out how your child learns best — then builds a path that works for them, not a one-size-fits-all plan.</p></FadeIn>
        </div>
      </section>

      {/* ── BEFORE / AFTER ── */}
      <section className="mx-auto max-w-4xl px-6 py-20">
        <FadeIn><p className="mb-10 text-center font-serif text-2xl font-normal italic leading-relaxed text-slate-800 md:text-3xl">Finally see how your child is doing — without guessing.</p></FadeIn>
        <div className="grid gap-4 md:grid-cols-2">
          <FadeIn delay={0.05}>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-7">
              <span className="mb-4 inline-block rounded-full bg-slate-200 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">Before Patharo</span>
              <h3 className="mb-5 font-serif text-xl font-normal text-slate-900">Without Patharo</h3>
              {['Report card says \u201cmeeting expectations\u201d \u2014 but what does that mean?', "Your child studied hard and still struggled on the test.", "You don't know how to help with French Immersion homework.", "Every app gives the same generic practice as everyone else."].map((t) => (<div key={t} className="mb-3 flex items-start gap-2.5"><span className="mt-0.5 shrink-0 text-xs text-red-400">✕</span><p className="text-sm leading-relaxed text-slate-500">{t}</p></div>))}
            </div>
          </FadeIn>
          <FadeIn delay={0.12}>
            <div className="rounded-3xl border border-white/10 bg-slate-900 p-7">
              <span className="mb-4 inline-block rounded-full bg-emerald-500/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300">With Patharo</span>
              <h3 className="mb-5 font-serif text-xl font-normal text-white">With Patharo</h3>
              {["See exactly which skills need work — subject by subject.", "Polo finds the gap and guides the right practice, not more of the same.", "Supports English and French Immersion, independently.", "A personal path that adapts to how your child actually learns."].map((t) => (<div key={t} className="mb-3 flex items-start gap-2.5"><span className="mt-0.5 shrink-0 text-xs text-emerald-400">✓</span><p className="text-sm leading-relaxed text-white/70">{t}</p></div>))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── POLO INTRO ── */}
      <section className="border-b border-slate-200 bg-white px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:items-center">
            <FadeIn>
              <div className="flex items-center justify-center">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-blue-100 opacity-60 blur-3xl scale-110" />
                  <div className="relative flex flex-col items-center gap-4 rounded-3xl border border-slate-100 bg-gradient-to-br from-blue-50 to-cyan-50/60 px-12 py-10 shadow-sm">
                    <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 shadow-lg shadow-blue-200"><span className="text-4xl select-none">🐾</span></div>
                    <div className="text-center"><p className="font-serif text-lg font-medium text-slate-900">Polo</p><p className="text-xs text-slate-400">Your child&apos;s AI tutor</p></div>
                    <div className="w-full max-w-[220px] rounded-2xl rounded-tl-sm bg-white px-4 py-3 shadow-sm ring-1 ring-slate-100"><p className="text-sm leading-relaxed text-slate-700">Hey Olivia! Let&apos;s tackle fractions today — I think I know exactly where it&apos;s getting tricky. 🎯</p></div>
                    <div className="flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700"><span>⭐</span> +40 XP — 3 in a row!</div>
                  </div>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div>
                <span className="mb-4 inline-block text-xs font-bold uppercase tracking-[0.16em] text-blue-600">Meet Polo</span>
                <h2 className="font-serif text-3xl font-normal leading-snug tracking-tight text-slate-900 md:text-4xl">A tutor who knows your child — not just the curriculum.</h2>
                <p className="mt-5 text-base leading-relaxed text-slate-500">Polo is the AI guide at the heart of Patharo. Patient, encouraging, and always matched to what Ontario schools actually teach — Polo meets your child where they are and moves with them as they grow.</p>
                <div className="mt-7 space-y-3">
                  {[{ icon: "💬", label: "Explains concepts in plain language", desc: "No jargon. No confusion. Just clear, warm explanations your child will actually understand." }, { icon: "🎯", label: "Targets the exact gap — not the whole topic", desc: "Polo doesn't restart from chapter one. It finds the specific part that isn't clicking." }, { icon: "🌟", label: "Celebrates every win", desc: "XP, streaks, and genuine encouragement — Polo makes progress feel good at every age." }].map((item) => (<div key={item.label} className="flex gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4"><span className="mt-0.5 text-xl">{item.icon}</span><div><p className="text-sm font-semibold text-slate-800">{item.label}</p><p className="mt-0.5 text-xs leading-relaxed text-slate-500">{item.desc}</p></div></div>))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-slate-50 px-6 py-20">
        <div className="mx-auto max-w-2xl">
          <FadeIn><div className="mb-10 text-center"><span className="mb-3 inline-block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Common questions</span><h2 className="font-serif text-3xl font-normal tracking-tight text-slate-900">Things parents usually ask</h2></div></FadeIn>
          <div className="space-y-3">{FAQ_ITEMS.map((item, i) => (<FadeIn key={i} delay={i * 0.05}><FaqItem q={item.q} a={item.a} /></FadeIn>))}</div>
          <FadeIn delay={0.3}><p className="mt-10 text-center text-sm text-slate-400">Something else on your mind?{" "}<a href="mailto:hello@patharo.ca" className="font-medium text-blue-600 hover:underline">Send us a message</a></p></FadeIn>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="bg-slate-900 px-6 py-20">
        <div className="mx-auto max-w-lg text-center">
          <FadeIn>
            <span className="mb-5 inline-block rounded-full bg-white/[0.08] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-200/80">Join the waitlist</span>
            <h2 className="font-serif text-3xl font-normal leading-snug text-white md:text-4xl">Your child&apos;s next step<br />starts here.</h2>
            <p className="mx-auto mt-4 max-w-sm text-sm leading-7 text-white/40">Tell us what grade they&apos;re in and we&apos;ll reach out to you first — with content built for exactly that year.</p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div className="mt-8">
              {sub2Done ? (
                <div className="rounded-2xl bg-emerald-900/40 px-6 py-4 text-sm font-medium text-emerald-300">🎉 You&apos;re on the waitlist — you&apos;ll hear from us first.</div>
              ) : (
                <div className="mx-auto flex max-w-sm flex-col gap-2.5">
                  <input type="email" placeholder="Your email address" value={email2} onChange={(e) => setEmail2(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleWaitlist()} className="h-12 rounded-xl border border-white/[0.14] bg-white/[0.06] px-4 text-sm text-white outline-none placeholder:text-white/30 focus:border-blue-400 focus:ring-2 focus:ring-blue-900"/>
                  <select value={grade2} onChange={(e) => setGrade2(e.target.value)} className="h-12 rounded-xl border border-white/[0.14] bg-white/[0.06] px-4 text-sm text-white/70 outline-none focus:border-blue-400" style={{ colorScheme: "dark" }}>
                    <option value="">What grade is your child?</option>
                    {GRADES.map((g) => (<option key={g} value={g} className="bg-slate-800 text-white">{g}</option>))}
                  </select>
                  <button onClick={handleWaitlist} disabled={sub2Loading || !email2} className="h-12 rounded-xl bg-blue-600 text-sm font-semibold text-white shadow-lg shadow-blue-900/40 transition hover:bg-blue-500 disabled:opacity-50">{sub2Loading ? "One moment…" : "Reach out to me first →"}</button>
                  <p className="text-[11px] text-white/25">Free · No commitment · We&apos;ll contact you when your grade is ready</p>
                </div>
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#070D18] px-6 py-10">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 flex flex-wrap items-start justify-between gap-8">
            <div><p className="font-serif text-lg font-medium text-white">Patharo</p><p className="mt-1 text-[11px] text-white/30">English &amp; French Immersion · Grades 1–12 · Ontario</p></div>
            <div className="flex gap-12">
              <div><p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-white/30">Product</p><div className="space-y-2"><Link href="/onboarding" className="block text-sm text-white/50 hover:text-white transition">Create your plan</Link><Link href="/login" className="block text-sm text-white/50 hover:text-white transition">Log in</Link></div></div>
              <Link href="/about" className="block text-sm text-white/50 hover:text-white transition">About</Link>
              <Link href="/blog" className="block text-sm text-white/50 hover:text-white transition">Blog</Link>
            </div>
          </div>
          <div className="border-t border-white/[0.06] pt-6"><p className="text-[11px] text-white/20">© {new Date().getFullYear()} Patharo. All rights reserved.</p></div>
        </div>
      </footer>
    </main>
  );
}